import { useNavigate } from "react-router-dom";
import { create } from "../../api/event";
import { FormComponent } from "../../component/form/FormComponent"
import { useAuth } from "../../context/authContext"
import { useLoading } from "../../context/loadingContext";
import { Field, FormReturnData } from "../../types/FromComponent.types"
import { useNotification } from "../../context/notificationContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as ReportTypeAPI from "../../api/reportType";
import { isSuccessCode } from "../../helpers/apiResponse";
import * as TeamAPI from "../../api/team";
import { EventType } from "../../types/api/event.type";

type ReportTypeItem = {
    id: number;
    name: string;
};

type TeamItem = {
    id: number;
    name: string;
};

export const AddEventScreen = () => {
    const { association, associationRole } = useAuth();
    const { setLoadingStatus } = useLoading();
    const { addNotification } = useNotification();
    const nav = useNavigate();

    const toTeam = (item: Record<string, unknown>): TeamItem => ({
    id: Number(item.id ?? item.team_id ?? 0),
    name: String(item.name ?? item.team_name ?? ""),
});

    const [reportTypes, setReportTypes] = useState<ReportTypeItem[]>([]);
    const [teams, setTeams] = useState<TeamItem[]>([]);

    const toReportType = (item: Record<string, unknown>): ReportTypeItem => ({
        id: Number(item.id ?? item.report_type_id ?? 0),
        name: String(item.name ?? item.report_type_name ?? ""),
    });

    const eventTypes = useMemo(() => {
        if (associationRole === 'athlete') return [{ id: 'solo_training' as EventType, value: 'Solo Training' }];
        if (['coach', 'president'].includes(associationRole)) {
            return [{ id: 'training' as EventType, value: 'Training' }, { id: 'competition' as EventType, value: 'Competition' }];
        }
        return [{ id: 'official' as EventType, value: 'Official' }];
    }, [associationRole]);

    const handleApiError = useCallback((requestId: string, errorCode: string) => {
        nav(`/500?process_id=${requestId}&error_code=${errorCode}`);
    }, [nav]);

    const eventTypeLabel = associationRole === 'athlete' ? 'Create Personal Event' : 'Create Official Event';
    const isAthlete = associationRole === 'athlete';

    const handleSubmit = async (data: FormReturnData) => {
        setLoadingStatus(true, false);
        const request = await create(association.id,
            {
                title: data.title + "",
                date_start: (new Date(data.start_date)),
                date_end: (new Date(data.end_date)),
                type: data.event_type,
                description: data.description + "",
                location: data.location + "", 
                report_type_id: Number(data.report_type_id ?? 0),
                ...(isAthlete ? {} : { team_id: Number(data.team_id ?? 0) }),
            })
        switch (request.error_code) {
            case '08.04.01':
                addNotification("Success", "Event created successfully", 'success');
                setLoadingStatus(false);
                nav("/events");
                break;
            case '08.04.23':
                addNotification("Form error", "End date is required", 'error');
                setLoadingStatus(false);
                break;
            case '08.04.22':
                addNotification("Form error", "Start date is required", 'error');
                setLoadingStatus(false);
                break;
            case '08.04.21':
                addNotification("Form error", "Event title is required", 'error');
                setLoadingStatus(false);
                break;
            default:
                addNotification("Error", "An error occurred while creating the event", 'error');
                setLoadingStatus(false);
                break;
        }

    }

    const loadReportTypes = useCallback(async () => {
        const response = await ReportTypeAPI.list(association.id + "");
        if (!isSuccessCode(response.error_code)) {
            return handleApiError(response.request_id, response.error_code);
        }

        const mapped = (Array.isArray(response.data) ? response.data : [])
            .map((item: Record<string, unknown>) => toReportType(item))
            .filter((item) => item.id > 0);
        setReportTypes(mapped);
    }, [association.id, handleApiError]);

    useEffect(() => {
        setLoadingStatus(true);
        loadReportTypes().finally(() => setLoadingStatus(false));
    }, [loadReportTypes, setLoadingStatus]);


    const loadTeams = useCallback(async () => {
        const response = await TeamAPI.list();
        if (!isSuccessCode(response.error_code)) {
            return handleApiError(response.request_id, response.error_code);
        }

        const mapped = (Array.isArray(response.data) ? response.data : [])
            .map((item: Record<string, unknown>) => toTeam(item))
            .filter((item) => item.id > 0);
        setTeams(mapped);
    }, [handleApiError]);

    useEffect(() => {
        setLoadingStatus(true);
        loadTeams().finally(() => setLoadingStatus(false));
    }, [loadTeams, setLoadingStatus]);

    const fields: Field[] = [
        { name: "title", placeholder: "Event title", type: "text" },
        { name: "start_date", placeholder: "Event start date", type: 'date' },
        { name: "end_date", placeholder: "Event end date", type: "date" },
        { name: "description", placeholder: "Event description (optional)", type: "text", optional: true },
        { name: "location", placeholder: "Event location (optional)", type: "text", optional: true },
        { name: "event_type", placeholder: eventTypeLabel, type: "dropdown", options: eventTypes },
        { name: "report_type_id", placeholder: "Report type", type: "dropdown", options: reportTypes.map(rt => ({ id: rt.id, value: rt.name })), optional: true },
        ...(!isAthlete ? [{ name: "team_id", placeholder: "Team", type: "dropdown", options: teams.map(team => ({ id: team.id, value: team.name })), optional: true } satisfies Field] : []),
    ]

    return (
        <FormComponent fields={fields} onSubmit={handleSubmit} title={eventTypeLabel} button={{ name: "Create Event" }} />
    )
}
